import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import StatusPill from '../components/ui/StatusPill';
import { SkeletonMachineCard, SkeletonStatTile } from '../components/ui/Skeleton';

import { Icon, ICONS as BASE_ICONS } from '../components/ui/Icon';

const ICONS = {
  ...BASE_ICONS,
  total:   'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18',
  active:  BASE_ICONS.checkCircle,
  offline: BASE_ICONS.xCircle,
  water:   'M12 2.69l5.66 5.66a8 8 0 11-11.31 0z',
  arrow:   BASE_ICONS.arrow,
  open:    BASE_ICONS.externalLink,
};

// ── Flash hook ────────────────────────────────────────────────────────────────
function useFlash(value) {
  const [flashing, setFlashing] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current !== value) {
      prev.current = value;
      setFlashing(true);
      const t = setTimeout(() => setFlashing(false), 800);
      return () => clearTimeout(t);
    }
  }, [value]);
  return flashing;
}

// ── Stat tile ─────────────────────────────────────────────────────────────────
function StatTile({ label, value, sub, icon, color, delay = 0 }) {
  const flashing = useFlash(value);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-hair)',
        borderRadius: 'var(--r-lg)',
        padding: '20px 22px',
        boxShadow: 'var(--sh-sm)',
      }}
    >
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--ink-500)', marginBottom: 10 }}>
        {label}
      </div>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 34, fontWeight: 500,
        letterSpacing: '-1px', color,
        borderRadius: 6, padding: '0 2px',
        animation: flashing ? 'atoms-flash 0.8s ease-out' : 'none',
      }}>
        {value ?? '—'}
      </div>
      {sub && (
        <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-500)', marginTop: 4 }}>{sub}</div>
      )}
    </motion.div>
  );
}

// ── Tiny sparkline (SVG) ──────────────────────────────────────────────────────
function Sparkline({ points = [], color = 'var(--accent)', width = 80, height = 28 }) {
  if (!points.length) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const W = width, H = height;
  const xs = points.map((_, i) => (i / (points.length - 1)) * W);
  const ys = points.map(v => H - ((v - min) / range) * (H - 4) - 2);
  const d = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const fill = `${d} L${W},${H} L0,${H} Z`;
  return (
    <svg width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
      <path d={fill} fill={color} opacity={0.12} />
      <path d={d} stroke={color} strokeWidth={1.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Tank bar ─────────────────────────────────────────────────────────────────
function TankBar({ pct = 0 }) {
  const p = Math.max(0, Math.min(100, pct));
  const color = p < 25 ? 'var(--warn)' : 'var(--accent)';
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 10, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Tank</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color }}>{p}%</span>
      </div>
      <div style={{ height: 4, background: 'var(--border-subtle)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${p}%`, background: color, borderRadius: 4, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

// ── Machine card ──────────────────────────────────────────────────────────────
function MachineCard({ machine, index, onClick }) {
  const isActive = machine.Machines_status === 'Active';
  const metrics  = machine.metrics || [];
  const variant  = isActive ? 'online' : 'offline';
  const tankPct  = machine.tank_level ?? null;
  const sparkPts = machine.sparkline || [];
  const aiAlert  = machine.ai_alert || null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05 + index * 0.04 }}
      className={`machine-card ${variant}`}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0, marginRight: 10 }}>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {machine.Machine_name}
          </div>
          {machine.Machine_ID !== machine.Machine_name && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {machine.Machine_ID}
            </div>
          )}
          {machine.org_name && (
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, color: 'var(--ink-300)', marginTop: 1 }}>{machine.org_name}</div>
          )}
        </div>
        <StatusPill variant={variant} />
      </div>

      {/* KPI metrics */}
      {isActive && metrics.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
          {metrics.slice(0, 2).map((m, i) => (
            <FlashMetric key={i} name={m.name} value={m.value} />
          ))}
        </div>
      ) : !isActive ? (
        <div style={{ fontSize: 12, color: 'var(--ink-300)', marginTop: 8, fontFamily: 'var(--font-sans)' }}>
          {machine.last_seen ? `Last seen ${machine.last_seen}` : 'No data received today'}
        </div>
      ) : null}

      {/* Tank bar */}
      {isActive && tankPct !== null && <TankBar pct={tankPct} />}

      {/* Sparkline row */}
      {isActive && sparkPts.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 10, color: 'var(--ink-300)' }}>9H</span>
          <Sparkline points={sparkPts} />
        </div>
      )}

      {/* AI insight strip */}
      {aiAlert && (
        <div style={{
          marginTop: 10, paddingTop: 10,
          borderTop: '1px dashed var(--ai-soft)',
          display: 'flex', alignItems: 'flex-start', gap: 7,
        }}>
          <Icon d={ICONS.spark} size={13} color="var(--ai)" sw={1.8} />
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ai-ink)', lineHeight: 1.4 }}>
            {aiAlert}
          </div>
        </div>
      )}

      {/* Arrow */}
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, color: 'var(--ink-300)', fontSize: 11, fontFamily: 'var(--font-sans)' }}>
        View details <Icon d={ICONS.arrow} size={12} color="var(--ink-300)" />
      </div>
    </motion.div>
  );
}

function FlashMetric({ name, value }) {
  const flashing = useFlash(value);
  return (
    <div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--ink-500)', marginBottom: 3 }}>{name}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 600, color: 'var(--ink-900)', borderRadius: 4, padding: '0 2px', animation: flashing ? 'atoms-flash 0.8s ease-out' : 'none' }}>
        {value ?? '—'}
      </div>
    </div>
  );
}

// ── Machine Intelligence panel ────────────────────────────────────────────────
function AlertRow({ m }) {
  const isOffline = m.status === 'offline';
  const bg    = isOffline ? 'var(--offline-bg)'  : 'var(--warn-bg)';
  const border = isOffline ? 'var(--offline-border)' : 'var(--warn-border)';
  const dot   = isOffline ? 'var(--offline)' : 'var(--warn)';
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 'var(--r-md)', padding: '12px 16px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0, marginTop: 4 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 13, color: 'var(--ink-900)' }}>{m.name}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)' }}>{m.id}</span>
            </div>
            <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 12, color: dot, marginTop: 2 }}>{m.headline}</div>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.5 }}>{m.detail}</div>
          </div>
        </div>
        {m.action && (
          <button style={{
            flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5,
            background: 'var(--ai-soft)', border: '1px solid rgba(105,56,239,0.2)',
            borderRadius: 'var(--r-sm)', padding: '4px 10px',
            fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ai)', cursor: 'pointer',
          }}>
            <Icon d={ICONS.spark} size={11} color="var(--ai)" />
            {m.action}
          </button>
        )}
      </div>
    </div>
  );
}

function MachineIntelligence({ machines, wsStatus }) {
  const alerts = machines.filter(m =>
    m.Machines_status !== 'Active' || m.ai_alert
  ).slice(0, 5).map(m => ({
    name:     m.Machine_name,
    id:       m.Machine_ID,
    status:   m.Machines_status === 'Active' ? 'warn' : 'offline',
    headline: m.Machines_status !== 'Active'
      ? `Offline — no data received`
      : m.ai_alert || '',
    detail:   m.Machines_status !== 'Active'
      ? 'Machine is not sending telemetry. Check power and network at site.'
      : '',
    action:   m.Machines_status !== 'Active' ? 'Notify site contact' : 'Compare readings',
  }));

  if (!alerts.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.15 }}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-hair)',
        borderRadius: 'var(--r-lg)',
        padding: '18px 20px',
        boxShadow: 'var(--sh-sm)',
        backgroundImage: 'linear-gradient(100deg, var(--ai-soft) 0%, transparent 34%)',
        marginBottom: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Shimmer AI mark */}
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: 'var(--ai)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 'var(--sh-ai)',
          }}>
            <Icon d={ICONS.spark} size={14} color="#fff" sw={2} />
          </div>
          <div>
            <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 13, color: 'var(--ink-900)' }}>
              Machine Intelligence
            </span>
            <span style={{
              marginLeft: 8, fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600,
              background: 'var(--ai-soft)', color: 'var(--ai)',
              border: '1px solid rgba(105,56,239,0.2)', borderRadius: 4, padding: '2px 7px',
            }}>BETA</span>
          </div>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-500)' }}>
            Watching {machines.length} machines · {wsStatus === 'live' ? 'live' : 'syncing'}
          </span>
        </div>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--offline)' }}>
          {alerts.length} need attention
        </span>
      </div>
      {alerts.map((a, i) => <AlertRow key={i} m={a} />)}
    </motion.div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyMachines() {
  return (
    <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', gap: 12 }}>
      <Icon d={ICONS.total} size={32} color="var(--border-strong)" />
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 500, color: 'var(--ink-700)' }}>No machines found</div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)' }}>No machines are assigned to your account.</div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function DashBoardPage() {
  const navigate = useNavigate();
  const [totalCount,    setTotalCount]    = useState({});
  const [machineStatus, setMachineStatus] = useState([]);
  const [wsStatus,      setWsStatus]      = useState('connecting');
  const [hasLoaded,     setHasLoaded]     = useState(false);
  const wsRef = useRef(null);

  const connect = () => {
    setWsStatus('connecting');
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url   = `${proto}//${window.location.host}/dashboardSocket/?user_id=${localStorage.getItem('userid')}`;
    const ws    = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsStatus('live');
      ws.send(JSON.stringify({ message: 'CONNECTED', user_id: localStorage.getItem('userid'), user_name: localStorage.getItem('username') }));
    };
    ws.onmessage = (e) => {
      const b = JSON.parse(e.data);
      setTotalCount(b.total_count_result || {});
      const active = [], inactive = [];
      (b.machine_status || []).forEach(m =>
        m.Machines_status === 'Inactive' ? inactive.push(m) : active.push(m)
      );
      const sorted = [...active, ...inactive];
      setMachineStatus(sorted);
      // Cache for ⌘K palette
      try { sessionStorage.setItem('atoms_machines', JSON.stringify(sorted)); } catch {}
      setHasLoaded(true);
    };
    ws.onerror = () => setWsStatus('disconnected');
    ws.onclose = () => setWsStatus(s => s !== 'disconnected' ? 'disconnected' : s);
  };

  useEffect(() => { connect(); return () => wsRef.current?.close(); }, []); // eslint-disable-line

  const counts = {
    total:    totalCount['Total Machines']    ?? totalCount['total']    ?? '—',
    active:   totalCount['Active Machines']   ?? totalCount['active']   ?? '—',
    inactive: totalCount['Inactive Machines'] ?? totalCount['inactive'] ?? '—',
    water:    totalCount['Water Today']       ?? totalCount['water_today'] ?? null,
  };

  const hasAlerts = machineStatus.some(m => m.Machines_status !== 'Active' || m.ai_alert);

  return (
    <div style={{ maxWidth: 1320, margin: '0 auto' }}>

      {/* ── Page head ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 25, letterSpacing: '-0.5px', color: 'var(--ink-900)', marginBottom: 4 }}>
            Overview
          </h1>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-500)' }}>
            Real-time state across every assigned machine.
          </p>
        </div>
        {wsStatus !== 'live' && (
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: wsStatus === 'connecting' ? 'var(--warn)' : 'var(--offline)' }}>
            {wsStatus === 'connecting' ? 'Connecting…' : (
              <button onClick={connect} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontFamily: 'inherit', fontSize: 'inherit', padding: 0 }}>Disconnected — click to retry</button>
            )}
          </div>
        )}
      </div>

      {/* ── Stat strip ── */}
      {!hasLoaded ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--grid-gap)', marginBottom: 28 }}>
          {[0,1,2,3].map(i => <SkeletonStatTile key={i} />)}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: counts.water != null ? 'repeat(4,1fr)' : 'repeat(3,1fr)', gap: 'var(--grid-gap)', marginBottom: 28 }}>
          <StatTile label="Total Machines"  value={counts.total}    icon={ICONS.total}   color="var(--ink-900)"  delay={0}    />
          <StatTile label="Active Now"      value={counts.active}   icon={ICONS.active}  color="var(--online)"   delay={0.06} />
          <StatTile label="Offline"         value={counts.inactive} icon={ICONS.offline} color="var(--offline)"  delay={0.12} />
          {counts.water != null && (
            <StatTile label="Water Today"   value={counts.water}    icon={ICONS.water}   color="var(--accent)"   delay={0.18}
              sub="litres · fleet total" />
          )}
        </div>
      )}

      {/* ── Machine Intelligence panel ── */}
      {hasLoaded && hasAlerts && (
        <MachineIntelligence machines={machineStatus} wsStatus={wsStatus} />
      )}

      {/* ── Section label ── */}
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--ink-500)', marginBottom: 12 }}>
        Machine Status · {machineStatus.length || '—'}
      </div>

      {/* ── Machine grid ── */}
      {!hasLoaded ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 'var(--grid-gap)' }}>
          {[0,1,2,3,4,5].map(i => <SkeletonMachineCard key={i} />)}
        </div>
      ) : machineStatus.length === 0 ? (
        <EmptyMachines />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 'var(--grid-gap)' }}>
          <AnimatePresence>
            {machineStatus.map((m, i) => (
              <MachineCard
                key={m.Machine_ID || m.node_id}
                machine={m}
                index={i}
                onClick={() => navigate(`/app/submachine/${m.Machine_name}/${m.Machine_ID}`)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
